import { types, applySnapshot } from "mobx-state-tree";

import CONSTANTS from "const/constants";
import { DEFAULT_STUDIO_USER_PROFILE_IMAGE_URL } from "const/images";

declare const App: any;

const {
    CREDENTIAL_TYPES,
    CREDENTIAL_TYPE_LABELS,
    SOCIAL_CREDENTIAL_STATUS,
    TWITTER_PRO_MEDIA_API_FEATURES,
    SOCIAL_CREDENTIAL_FEATURES
} = CONSTANTS;

const model = {
    organizationCredentialId: types.identifier,
    organizationId: types.string,
    displayName: types.maybe(types.string),
    status: types.maybe(types.string),
    integration: types.maybe(types.frozen({})),
    access: types.array(types.string),
    addedBy: types.maybe(types.string),
    creationDate: types.maybe(types.number)
};

const volatile = () => ({});

const views = (self) => ({
    get isActive() {
        return self.status === SOCIAL_CREDENTIAL_STATUS.ACTIVE;
    },
    get isEditable() {
        return self.isEditEnabled;
    },
    get type() {
        return self.integration.type;
    },
    get typeLabel() {
        const key = Object.keys(CREDENTIAL_TYPES).find(
            (k) => CREDENTIAL_TYPES[k] === self.type
        );
        return CREDENTIAL_TYPE_LABELS[key] ?? ``;
    },
    get assignedCount() {
        return self?.getActiveAccessList?.()?.length ?? 0;
    },
    get profileImageUrl() {
        switch (self.type) {
            case CREDENTIAL_TYPES.TWITTER:
                return self.integration?.profileImageUrl;
            case CREDENTIAL_TYPES.FACEBOOK:
                return self.integration?.pageProfileImageUrl;
            case CREDENTIAL_TYPES.TIKTOK:
                return self.integration?.avatar;
            case CREDENTIAL_TYPES.INSTAGRAM:
                return self.integration?.profilePictureUrl;
            case CREDENTIAL_TYPES.TWITCH:
                return self.integration?.channelLogoUrl;
            default:
                return DEFAULT_STUDIO_USER_PROFILE_IMAGE_URL;
        }
    },

    get twitterProMediaApiFeatures() {
        return self.integration?.proMediaApiFeatures ?? [];
    },

    get twitterProMediaEnabled() {
        // Does the user have access to the Pro Media API?
        return !!self?.twitterProMediaApiFeatures?.length;
    },

    get twitterVideoAdsEnabled() {
        if (!self.twitterProMediaEnabled) {
            return false;
        }

        return (
            self.twitterProMediaApiFeatures?.includes?.(
                TWITTER_PRO_MEDIA_API_FEATURES.MEDIATOOL_MONETIZATION_USER
            ) ?? false
        );
    },

    get twitterSponsorshipsEnabled() {
        if (!self.twitterProMediaEnabled) {
            return false;
        }

        return (
            self.twitterProMediaApiFeatures?.includes?.(
                TWITTER_PRO_MEDIA_API_FEATURES.MEDIATOOL_AMPLIFY_DIRECT
            ) ?? false
        );
    },

    get twitterMonetizationEnabled() {
        return self.twitterVideoAdsEnabled || self.twitterSponsorshipsEnabled;
    },

    get youtubeContentIdApiEnabled() {
        return (
            self.type === CREDENTIAL_TYPES.YOUTUBE &&
            (self.integration?.contentIdAccessTokenExpiryDate ?? false)
        );
    },

    get isMonetizationEnabled() {
        switch (self.type) {
            case CREDENTIAL_TYPES.TWITTER:
                return self.twitterMonetizationEnabled;
            case CREDENTIAL_TYPES.YOUTUBE:
                return self.youtubeContentIdApiEnabled;
            default:
                return false;
        }
    },

    get isReauthenticateEnabled() {
        return [
            CREDENTIAL_TYPES.FACEBOOK,
            CREDENTIAL_TYPES.TWITTER,
            CREDENTIAL_TYPES.SPREDFAST,
            CREDENTIAL_TYPES.YOUTUBE,
            CREDENTIAL_TYPES.TIKTOK,
            CREDENTIAL_TYPES.INSTAGRAM,
            CREDENTIAL_TYPES.TWITCH,
            CREDENTIAL_TYPES.THREADS
        ].includes(self.type);
    },

    get isRefreshEnabled() {
        return [
            CREDENTIAL_TYPES.YOUTUBE,
            CREDENTIAL_TYPES.TIKTOK,
            CREDENTIAL_TYPES.TWITCH
        ].includes(self.type);
    },

    get isEditEnabled() {
        return [
            CREDENTIAL_TYPES.FTP,
            CREDENTIAL_TYPES.S3,
            CREDENTIAL_TYPES.CUSTOM_RTMP,
            CREDENTIAL_TYPES.BRIGHTCOVE,
            CREDENTIAL_TYPES.THEPLATFORM,
            CREDENTIAL_TYPES.OPTA,
            CREDENTIAL_TYPES.GOOGLE_CLOUD_PLATFORM
        ].includes(self.type);
    },

    get features() {
        const features = [];
        self.youtubeContentIdApiEnabled &&
            features.push(SOCIAL_CREDENTIAL_FEATURES.CONTENT_ID);
        self.twitterMonetizationEnabled &&
            features.push(SOCIAL_CREDENTIAL_FEATURES.MONETIZATION);
        return features;
    },

    getActiveAccessList() {
        const activeUsers =
            App.Store?.users?.getActiveUsers?.().map((user) => user.userId) ??
            [];
        const activeAccessList =
            self?.access?.filter?.((userId) => activeUsers.includes(userId)) ??
            [];
        return activeAccessList;
    },

    isAssignedToUser(userId) {
        return self.access.includes(userId);
    }
});

const actions = (self) => ({
    onCreate() {
        self.associateWithSocialCredential();
    },

    update(data) {
        if (data?.organizationCredentialId === self.organizationCredentialId) {
            applySnapshot(self, data);
        }
    }
});

const OrganizationCredentialStore = types
    .model(`OrganizationCredential`, model)
    .volatile(volatile)
    .actions(actions)
    .views(views);

export default OrganizationCredentialStore;
